/**
 * The application's QueryClient, and the global error surfaces attached to it.
 *
 * Extracted from `providers.tsx` so tests can build a client with the SAME
 * QueryCache/MutationCache handlers the app runs with. That matters more than it
 * looks: nearly every "silent 4xx" in this codebase is a query or mutation whose
 * failure is *supposed* to be caught here, and a test that hand-rolls a bare
 * `new QueryClient()` proves the happy path in a world the user never runs.
 *
 * Global handlers surface every failure as a toast unless the query/mutation
 * opts out with `meta: { suppressToast }`. Opting out is only legal when the
 * caller renders its own error surface — see `docs/suppress-toast-audit.md`.
 */

import { QueryCache, QueryClient, MutationCache, type QueryClientConfig } from '@tanstack/react-query'
import { DEFAULT_QUERY_STALE_TIME_MS } from '@/api/query-defaults'
import { useToastStore } from '@/components/ui/toast/toast'
import { toToastMessage } from '@/lib/errors'
import { captureUiError } from '@/lib/telemetry'
import { isSwitchStaleQuery } from '@/features/account/workspace-switch/switch-quiet-set'

/**
 * Reduce a TanStack query/mutation key to a Sentry-safe grouping label.
 *
 * Keys contain workspace/user IDs, cursor tokens, search text, and signed URL
 * params — none of which should leave the browser as free-form tag text. The
 * `beforeSend` scrubber only cleans request URLs and headers, so a raw key
 * would copy PII and short-lived credentials straight into telemetry.
 *
 * Only the first segment (the "family") is emitted. That is the stable
 * grouping label that distinguishes `['brands']` from `['projects']` without
 * exfiltrating per-call arguments. Anything deeper is dropped.
 */
function queryKeyTag(queryKey: readonly unknown[]): string {
  const first = queryKey[0]
  if (typeof first !== 'string' || first.length === 0) return 'unknown'
  return first.length > 64 ? `${first.slice(0, 61)}...` : first
}

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: {
      /** Skip the global error toast — the component renders the error itself. */
      suppressToast?: boolean
      /** Override the fallback copy used when the error has no extractable message. */
      errorFallback?: string
    }
    mutationMeta: {
      suppressToast?: boolean
      /** Skip the global console logger when the owning UI handles this error. */
      suppressErrorLog?: boolean
      errorFallback?: string
    }
  }
}

/** The app's global mutation error surface — exported so tests can attach the same cache. */
export function createAppMutationCache(): MutationCache {
  return new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (!mutation.meta?.suppressErrorLog) {
        console.error('[Mutation error]', error)
      }
      captureUiError(error, {
        surface: 'mutation',
        tags: {
          suppressedToast: Boolean(mutation.meta?.suppressToast),
          mutationKey: mutation.options.mutationKey
            ? queryKeyTag(mutation.options.mutationKey)
            : undefined,
        },
      })
      if (mutation.meta?.suppressToast) return
      const fallback = mutation.meta?.errorFallback ?? 'Action failed. Please try again.'
      useToastStore.getState().show(toToastMessage(error, fallback), 'error')
    },
  })
}

/** Shared defaults — see the per-option comments for why each one is set. */
const DEFAULT_OPTIONS: QueryClientConfig['defaultOptions'] = {
  queries: {
    staleTime: DEFAULT_QUERY_STALE_TIME_MS,
    gcTime: 30 * 60 * 1000, // 30 minutes
    // Refetch stale queries when the tab regains focus, so data changed by
    // another user (role/seat/credit/member changes) self-heals instead of
    // sitting stale until a hard refresh. Gated by staleTime — only queries
    // older than their staleTime refetch, so this stays low-noise.
    refetchOnWindowFocus: true,
    retry: 1,
  },
}

/**
 * A QueryClient wired with the app's global error toasts.
 *
 * `overrides` merges over the shared defaults — tests typically disable retries
 * so a failure surfaces immediately.
 */
export function createAppQueryClient(overrides: QueryClientConfig = {}): QueryClient {
  return new QueryClient({
    ...overrides,
    queryCache: new QueryCache({
      onError: (error, query) => {
        console.error('[Query error]', query.queryKey, error)
        // When `applyWorkspaceChange` cancels + resets workspace-scoped queries,
        // the still-mounted outgoing route fires one last refetch under the new
        // scope and 4xx's with the old route's identifiers. Those exact query
        // hashes are tagged at switch time; everything else — including
        // destination-route queries started after the switch — keeps its toast
        // and Sentry capture.
        if (isSwitchStaleQuery(query)) return
        captureUiError(error, {
          surface: 'query',
          tags: {
            queryKey: queryKeyTag(query.queryKey),
            suppressedToast: Boolean(query.meta?.suppressToast),
          },
        })
        if (query.meta?.suppressToast) return
        const fallback = query.meta?.errorFallback ?? 'Failed to load data.'
        useToastStore.getState().show(toToastMessage(error, fallback), 'error')
      },
    }),
    mutationCache: createAppMutationCache(),
    defaultOptions: {
      ...DEFAULT_OPTIONS,
      ...overrides.defaultOptions,
      queries: { ...DEFAULT_OPTIONS?.queries, ...overrides.defaultOptions?.queries },
    },
  })
}
